import React, { createContext, useContext, useMemo, useState } from 'react';

import type { IndicatorBucket } from '../utils/indicatorBuckets';

export type MapColorMode = 'summary' | 'schedule' | 'mission' | 'finance' | 'data';

type Ctx = {
  selectedProjectGuid: string | null;
  setSelectedProjectGuid: React.Dispatch<React.SetStateAction<string | null>>;

  pulseProjectGuid: string | null;
  setPulseProjectGuid: React.Dispatch<React.SetStateAction<string | null>>;

  mapColorMode: MapColorMode;
  setMapColorMode: React.Dispatch<React.SetStateAction<MapColorMode>>;

  /** Optional filter from the map legend (semantic status bucket). */
  legendBucketFilter: IndicatorBucket | null;
  setLegendBucketFilter: React.Dispatch<React.SetStateAction<IndicatorBucket | null>>;
};

const ProjectSelectionContext = createContext<Ctx | null>(null);

export function ProjectSelectionProvider({ children }: { children: React.ReactNode }) {
  const [selectedProjectGuid, setSelectedProjectGuid] = useState<string | null>(null);
  const [pulseProjectGuid, setPulseProjectGuid] = useState<string | null>(null);
  const [mapColorMode, setMapColorMode] = useState<MapColorMode>('summary');
  const [legendBucketFilter, setLegendBucketFilter] = useState<IndicatorBucket | null>(null);

  const value = useMemo(
    () => ({
      selectedProjectGuid,
      setSelectedProjectGuid,
      pulseProjectGuid,
      setPulseProjectGuid,
      mapColorMode,
      setMapColorMode,
      legendBucketFilter,
      setLegendBucketFilter,
    }),
    [selectedProjectGuid, pulseProjectGuid, mapColorMode, legendBucketFilter]
  );

  return <ProjectSelectionContext.Provider value={value}>{children}</ProjectSelectionContext.Provider>;
}

export function useProjectSelection() {
  const ctx = useContext(ProjectSelectionContext);
  if (!ctx) throw new Error('useProjectSelection must be used within ProjectSelectionProvider');
  return ctx;
}
